import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../contexts/kato.context';
import { useShared } from '../../../contexts/shared.context';
import KatoSelects from '../components/kato-selects.component';
import FooterComponent from '../components/footer.component';
import Field from '../../../components/field/field.component';
import { toast } from 'react-toastify';
import { getTaxValue, isEmpty, t } from '../../../helpers/utils';
import {
  GeneralInfoStatus,
  ITaxStats,
  KatoType,
} from '../../../types/kato.types';
import {
  updateAutoLinkData,
  updateGeneralInfoData,
} from '../../../requests/supervisor.request';
import classes from './styles.module.scss';
import { useUser } from '../../../contexts/user.context';

const baseClass = 'tax-tab';
const translationKey = 'admin.tax';

const fields: (keyof ITaxStats)[] = [
  'fact',
  'taxRevenuesFact',
  'nonTaxRevenuesFact',
  'fixedAssetsSaleFact',
  'annual',
  'taxRevenuesAnnual',
  'nonTaxRevenuesAnnual',
  'fixedAssetsSaleAnnual',
  'plan',
  'taxRevenuesPlan',
  'nonTaxRevenuesPlan',
  'fixedAssetsSalePlan',
  'planPercent',
  'taxRevenuesPlanPercent',
  'nonTaxRevenuesPlanPercent',
  'fixedAssetsSalePlanPercent',
  'annualPercent',
  'taxRevenuesAnnualPercent',
  'nonTaxRevenuesAnnualPercent',
  'fixedAssetsSaleAnnualPercent',
];

const disabledFields: (keyof ITaxStats)[] = [
  'fact',
  'annual',
  'plan',
  'planPercent',
  'taxRevenuesPlanPercent',
  'nonTaxRevenuesPlanPercent',
  'fixedAssetsSalePlanPercent',
  'annualPercent',
  'taxRevenuesAnnualPercent',
  'nonTaxRevenuesAnnualPercent',
  'fixedAssetsSaleAnnualPercent',
];

const TaxTab = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo, currentLevel } = useKato();
  const { taxStats, status } = katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();
  const { modules } = useUser();

  const [formData, setFormData] = useState({
    taxStats: taxStats || [],
    status: status || GeneralInfoStatus.IN_PROCESS,
  });

  const [autolink, setAutolink] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const handleSave = () => {
    if (validateFields()) {
      if (modules.includes('autolink')) {
        const data = {
          incomeStats: { iframeUrl: autolink, kato: katoGeneralInfo.kato },
          status: formData.status,
        };
        updateAutoLinkData(katoGeneralInfo.kato, data, selectedInfoYear).then(
          () => {
            updatedGeneralInfo();
            toast.success(t(`admin.success`, language));
          },
        );
      } else {
        const data = {
          taxStats: formData.taxStats,
          status: formData.status,
        };
        updateGeneralInfoData(
          katoGeneralInfo.kato,
          data,
          selectedInfoYear,
        ).then(() => {
          updatedGeneralInfo();
          toast.success(t(`admin.success`, language));
        });
      }
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    for (const stat of formData.taxStats) {
      const requiredFields = fields.filter(
        (field) => !disabledFields.includes(field),
      );
      const filledFields = requiredFields.filter(
        (field) => !isEmpty(stat[field]),
      );
      const emptyFields = requiredFields.filter((field) =>
        isEmpty(stat[field]),
      );

      if (filledFields.length > 0 && emptyFields.length > 0) {
        for (const field of emptyFields) {
          newErrors[`${stat.type}.${field}`] = true;
        }
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const getType = (type: ITaxStats['type']) => {
    switch (currentLevel) {
      case KatoType.OBLAST:
        return type === 'REGIONAL_BUDGET'
          ? t(`${translationKey}.REGIONAL_BUDGET`)
          : t(`${translationKey}.PROPER_REGIONAL_BUDGET`);
      case KatoType.REGION:
        return type === 'REGIONAL_BUDGET'
          ? t(`${translationKey}.DISTRICT_BUDGET`)
          : t(`${translationKey}.PROPER_DISTRICT_BUDGET`);
      case KatoType.CITY:
        return t(`${translationKey}.CITY_BUDGET`);
      case KatoType.DISTRICT:
        return t(`${translationKey}.SO_BUDGET`);
      default:
        return t(`${translationKey}.REGIONAL_BUDGET`);
    }
  };

  useEffect(() => {
    setFormData({
      taxStats: taxStats || [],
      status: status || GeneralInfoStatus.IN_PROCESS,
    });
  }, [taxStats, katoGeneralInfo.kato, selectedInfoYear]);

  const renderFields = (lang: 'ru' | 'kz') => {
    return (
      <div className={classes['fields-container']}>
        {formData.taxStats.map((item: ITaxStats) => {
          return (
            <div key={item.type} className={classes['field-block']}>
              <div className={classes['field-block__title']}>
                {t(`${translationKey}.${item.type}`, lang)}
              </div>
              <div className={classes['field-block__fields']}>
                {fields.map((field) => {
                  const isDisabled = disabledFields.includes(field);
                  const value = isDisabled
                    ? getTaxValue(item, field)
                    : item[field];
                  return (
                    <Field
                      value={value}
                      key={`${item.type}-${field}-${lang}`}
                      type="number"
                      disabled={isDisabled}
                      label={t(`${translationKey}.${field}`, lang)}
                      onChange={(value) => {
                        setFormData((prev) => ({
                          ...prev,
                          taxStats: prev.taxStats.map((stat) =>
                            stat.type === item.type
                              ? { ...stat, [field]: value }
                              : stat,
                          ),
                        }));
                      }}
                      required
                      error={errors[item.type + '.' + field]}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!katoGeneralInfo || !katoGeneralInfo.kato || !taxStats) return null;

  return (
    <div className={classes[baseClass]}>
      {modules.includes('autolink') ? (
        <div>
          <Field
            value={autolink}
            type="text"
            label={t(`${translationKey}.autolink`)}
            onChange={(value) => setAutolink(value as string)}
            required
          />
        </div>
      ) : (
        <div className={classes[`grid`]}>
          <KatoSelects lang={language as 'ru' | 'kz'} />
          <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
          {renderFields(language as 'ru' | 'kz')}
          {renderFields(language === 'ru' ? 'kz' : 'ru')}
        </div>
      )}

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  );
};

export default TaxTab;
